import * as React from 'react';

interface RadioContextType {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
}

const RadioContext = React.createContext<RadioContextType>({});

export interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  onValueChange?: (value: string) => void;
  name?: string;
}

export function RadioGroup({
  value,
  onValueChange,
  name,
  className = '',
  children,
  ...props
}: RadioGroupProps) {
  const generatedName = React.useId();
  const groupName = name || generatedName;

  return (
    <RadioContext.Provider value={{ value, onChange: onValueChange, name: groupName }}>
      <div role="radiogroup" className={`shadcn-radio-group ${className}`.trim()} {...props}>
        {children}
      </div>
    </RadioContext.Provider>
  );
}

export interface RadioGroupItemProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> {
  value: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ value, label, description, className = '', id, disabled, ...props }, ref) => {
    const context = React.useContext(RadioContext);
    const isChecked = context.value === value;
    const generatedId = React.useId();
    const inputId = id || generatedId;

    return (
      <label
        htmlFor={inputId}
        className={`shadcn-radio-item-wrap ${isChecked ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${className}`.trim()}
      >
        <input
          type="radio"
          id={inputId}
          name={context.name}
          value={value}
          checked={isChecked}
          disabled={disabled}
          onChange={() => context.onChange?.(value)}
          className="shadcn-sr-only"
          ref={ref}
          {...props}
        />
        <div className={`shadcn-radio-circle ${isChecked ? 'checked' : ''}`} aria-hidden="true">
          {isChecked && <div className="shadcn-radio-dot" />}
        </div>
        {(label || description) && (
          <div className="shadcn-radio-text">
            {label && <span className="shadcn-radio-label">{label}</span>}
            {description && <span className="shadcn-radio-desc">{description}</span>}
          </div>
        )}
      </label>
    );
  }
);
RadioGroupItem.displayName = 'RadioGroupItem';
